import classNames from 'classnames';
import FJExpandIcon from '../../assets/expand.svg?react';
import FJStarEmptyIcon from '../../assets/star-empty.svg?react';
import FJStarFullIcon from '../../assets/star-full.svg?react';
import FJStarHalfIcon from '../../assets/star-half.svg?react';
import FJLocalStore from '../../store/FJLocalStore';
import { useViralScoreStars } from '../../hooks/useViralScoreStars';
import styles from './FJDetailsViralPanel.module.less';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';

interface FJDetailsViralPanelProps {
    score: number;
    summary: string;
    summaryExpanded?: boolean;
    onToggleSummary?: () => void;
}

const STAR_ICONS = {
    full: FJStarFullIcon,
    half: FJStarHalfIcon,
    empty: FJStarEmptyIcon,
} as const;

const STAR_ICON_CLASS = {
    full: styles.star_full,
    half: styles.star_half,
    empty: styles.star_empty,
} as const;

export default function FJDetailsViralPanel({
    score: scoreRaw,
    summary,
    summaryExpanded = false,
    onToggleSummary,
}: FJDetailsViralPanelProps) {
    const score = Math.max(0, Math.min(100, scoreRaw));
    const starKinds = useViralScoreStars(score);
    const titlePrefix = useMemo(() => {
        if (score < 55) return 'not-recommended';
        else if (score < 70) return 'needs-optimization';
        else if (score < 85) return 'publishable';
        else return 'high-potential';
    }, [score]);

    const summaryRef = useRef<HTMLDivElement>(null);
    /** 收起态下文案是否被 line-clamp 截断；展开时仍需展示按钮以便收起 */
    const [collapsedOverflow, setCollapsedOverflow] = useState(false);

    useLayoutEffect(() => {
        const el = summaryRef.current;
        if (!el) return;

        const measure = () => {
            if (summaryExpanded) return;
            setCollapsedOverflow(el.scrollHeight > el.clientHeight + 1);
        };

        measure();
        const ro = new ResizeObserver(() => {
            if (!summaryExpanded) measure();
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, [summary, summaryExpanded, titlePrefix]);

    const showExpandBtn = summaryExpanded || collapsedOverflow;
    const isHighLight = score >= 70;
    return (
        <div className={styles.root}>
            <div className={styles.score_column}>
                <div className={classNames(styles.score_box, isHighLight && styles.score_box_highlight)}>
                    <div className={styles.score_value}>
                        {score}
                        <span className={styles.score_slash}>/100</span>
                    </div>
                    <div className={styles.score_label}>{FJLocalStore._('viral-potential')}</div>
                    <div className={styles.stars_row} aria-hidden>
                        {starKinds.map((kind, i) => {
                            const Icon = STAR_ICONS[kind];
                            return (
                                <Icon
                                    key={i}
                                    className={classNames(styles.star_ico, STAR_ICON_CLASS[kind])}
                                    aria-hidden
                                />
                            );
                        })}
                    </div>
                </div>
            </div>

            <div className={classNames(styles.summary_column, !showExpandBtn && styles.summary_column_no_expand)}>
                <div
                    ref={summaryRef}
                    className={classNames(
                        styles.summary_text,
                        summaryExpanded ? styles.summary_expanded : styles.summary_collapsed,
                    )}
                >
                    <span>{FJLocalStore._(titlePrefix)} </span>
                    {summary}
                </div>
                {showExpandBtn ? (
                    <button
                        type='button'
                        className={styles.expand_btn}
                        aria-expanded={summaryExpanded}
                        onClick={() => onToggleSummary?.()}
                    >
                        <FJExpandIcon
                            className={classNames(styles.expand_icon, summaryExpanded && styles.expand_icon_up)}
                            aria-hidden
                        />
                    </button>
                ) : null}
            </div>
        </div>
    );
}
